/**
 * Navigation Item Component
 * Individual navigation item with active state and hover effects.
 * In collapsed mode, parent items with children show a flyout popover.
 */

import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Popover,
  List,
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { useState, useRef } from 'react';
import type { NavItemConfig } from './navItems';

interface NavItemProps {
  item: NavItemConfig;
  collapsed: boolean;
  level?: number;
}

const isPathActive = (itemPath: string, currentPath: string): boolean => {
  if (itemPath === '/') {
    return currentPath === '/';
  }
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
};

/**
 * Nav Item Component
 * Renders a single navigation item with optional children.
 * When collapsed with children, shows a popover flyout on click.
 */
export const NavItem: React.FC<NavItemProps> = ({ item, collapsed, level = 0 }) => {
  const location = useLocation();

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.path ? isPathActive(item.path, location.pathname) : false;

  // Check if any child is active
  const hasActiveChild = hasChildren
    ? item.children!.some((child) =>
        child.path ? isPathActive(child.path, location.pathname) : false
      )
    : false;

  // Popover anchor for collapsed flyout
  const anchorRef = useRef<HTMLLIElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleToggle = () => {
    if (hasChildren) {
      setPopoverOpen(!popoverOpen);
    }
  };

  const handlePopoverClose = () => {
    setPopoverOpen(false);
  };

  const Icon = item.icon;

  // If item has path and no children, render as link
  if (item.path && !hasChildren) {
    return (
      <ListItem
        disablePadding
        sx={{
          display: 'block',
          pl: level * 2,
        }}
      >
        <Tooltip
          title={collapsed ? item.label : ''}
          placement="right"
          disableHoverListener={!collapsed}
        >
          <ListItemButton
            component={NavLink}
            to={item.path}
            selected={isActive}
            sx={{
              minHeight: 48,
              justifyContent: collapsed ? 'center' : 'initial',
              px: 2.5,
              '&.Mui-selected': {
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                color: 'text.primary',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.16)',
                },
              },
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
            aria-label={item.label}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 'auto' : 3,
                justifyContent: 'center',
                color: isActive ? 'primary.main' : 'text.secondary',
              }}
            >
              <Icon />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 400,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  }

  // If item has children, render with flyout popover
  if (hasChildren) {
    return (
      <>
        <ListItem
          disablePadding
          ref={anchorRef}
          sx={{
            display: 'block',
            pl: level * 2,
          }}
        >
          <Tooltip
            title={collapsed && !popoverOpen ? item.label : ''}
            placement="right"
            disableHoverListener={!collapsed}
          >
            <ListItemButton
              onClick={handleToggle}
              selected={hasActiveChild}
              sx={{
                minHeight: 48,
                justifyContent: collapsed ? 'center' : 'initial',
                px: 2.5,
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  color: 'text.primary',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
              aria-label={`${item.label} menu`}
              aria-expanded={popoverOpen}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 'auto' : 3,
                  justifyContent: 'center',
                  color: hasActiveChild ? 'primary.main' : 'text.secondary',
                }}
              >
                <Icon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: hasActiveChild ? 600 : 400,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Flyout popover for collapsed sidebar */}
        <Popover
          open={popoverOpen}
          anchorEl={anchorRef.current}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                ml: 1,
                minWidth: 160,
                bgcolor: 'background.paper',
              },
            },
          }}
        >
          <List component="nav" disablePadding>
            <List disablePadding>
              {item.children!.map((child) => {
                const ChildIcon = child.icon;
                const childActive = child.path
                  ? isPathActive(child.path, location.pathname)
                  : false;

                return (
                  <ListItem key={child.id} disablePadding>
                    <ListItemButton
                      component={NavLink}
                      to={child.path || '#'}
                      selected={childActive}
                      onClick={handlePopoverClose}
                      sx={{
                        py: 1,
                        px: 2,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.12)',
                          '& .MuiListItemIcon-root': {
                            color: 'primary.main',
                          },
                        },
                        '&:hover': {
                          bgcolor: 'grey.100',
                        },
                      }}
                      aria-label={child.label}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          color: childActive ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        <ChildIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: childActive ? 600 : 400,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </List>
        </Popover>
      </>
    );
  }

  // Fallback (should not reach here)
  return null;
};
